/* eslint-disable import/no-unresolved */
import React, {
  useState,
  useMemo,
  useCallback,
  useRef,
  useEffect,
} from "react";
import PropTypes from "prop-types";
import _ from "lodash";
import { v4 as uuidv4 } from "uuid";
import usePrevious from "src/util/usePrevious";
import { useAuth0 } from "src/contexts/auth0Provider";
import { useAISidebar } from "src/hooks/useAISidebar";
import { EventBus, EVENTS } from "src/services/eventEmitter";
import {
  useApolloSettings,
  useTenantId,
} from "src/contexts/global/ApolloSettingsContext";
import {
  useWorkspaceFeatureFlags,
  useWorkspace,
} from "src/contexts/global/WorkspaceContext";

import { AGENTCORE_NAMES, SOURCE_TYPES } from "../consts";
import streamAIResponse from "../utils/streamAIResponse";
import { AIChatContext } from "./AIChatContext";
import useGetAIChat from "./hooks/useGetAIChat";

const markFailedPreviousAttempts = (messages) => {
  const userMessageIndex = _.findLastIndex(messages, (m) => {
    return m.role === "user";
  });
  return messages
    .map((m, i) => ({
      ...m,
      failed: m.failed || userMessageIndex < i,
    }))
    .filter((m) => m.failed)
    .map((m) => m.id);
};

export const AIChatProvider = ({ children, sourceId }) => {
  const authState = useAuth0();
  const workspace = useWorkspace();
  const { id: workspaceId } = workspace;
  const [question, setQuestion] = useState("");

  const [complete, setComplete] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isThinking, setIsThinking] = useState(false);
  const [error, setError] = useState(null);
  const [userHasScrolledUp, setUserHasScrolledUp] = useState(false);
  const [editingMessageId, setEditingMessageId] = useState(null);
  const { apiUrl } = useApolloSettings();
  const tenantId = useTenantId();

  const {
    chatId,
    chats,
    loading: chatsLoading,
    onStartNewChat,
    onSelectChat,
    adoptChatId,
    currentChat,
    updateChatOptimistically,
    onSubmitMessageFeedback,
    refetchChats,
  } = useGetAIChat();

  const [filteredMessageIds, setFilteredMessageIds] = useState([]);

  const rawMessages = currentChat?.messages;

  /** @type {{ versionId: string|null, updateSidebarState: Function }} AI sidebar state and controls */
  const { versionId } = useAISidebar();

  /** @type {{ enableAIChat: boolean }} Feature flags from workspace context */
  const { enableAIChat } = useWorkspaceFeatureFlags();
  /**
   * Determines if AI chat is enabled based on feature flags and source
   * @type {boolean}
   * @memoized
   */
  const aiEnabled = useMemo(
    () => enableAIChat && sourceId === SOURCE_TYPES.chat_q_and_a,
    [enableAIChat, sourceId]
  );

  useEffect(() => {
    if (error) {
      setFilteredMessageIds(markFailedPreviousAttempts(rawMessages || []));
    }
  }, [error, rawMessages]);

  const messages = useMemo(() => {
    let ms = [];
    if (rawMessages) {
      ms = rawMessages.filter((m) => !filteredMessageIds.includes(m.id));
    }

    return ms;
  }, [filteredMessageIds, rawMessages]);

  const previousMessages = usePrevious(messages);

  /**
   * Helper function to clear messages and reset state
   * @returns {void}
   */
  const onSessionClear = useCallback(() => {
    // Reset current question and state
    setUserHasScrolledUp(false);
    setError(null);
    setQuestion("");
    setComplete(true);
    setEditingMessageId(null);
  }, [setError]);

  /** @type {React.MutableRefObject<string|null>} Previous version ID for change detection */
  const previousVersionId = useRef(versionId);

  /**
   * Effect for managing chat session lifecycle based on versionId changes
   * @effect
   * @description Clears session when versionId changes from value to null,
   * sets loading state for new sessions, and tracks versionId changes
   * @param {Function} clearChatSession - Session clearing function
   * @param {string|null} versionId - Current version identifier
   * @param {number} messages.length - Number of current messages
   * @param {Function} setComplete - Completion state setter
   */
  useEffect(() => {
    // Clear session only when versionId changes from a value to null
    if (previousVersionId.current && !versionId) {
      onSessionClear();
    }

    previousVersionId.current = versionId;
  }, [onSessionClear, versionId, messages.length, setComplete]);

  const invokeAIResponse = useCallback(
    async (newQuestion, { isRetry = false } = {}) => {
      try {
        setQuestion("");
        setEditingMessageId(null);
        setIsSubmitting(true);
        setComplete(false);
        setIsThinking(true);

        // Optimistically show the user message immediately (skip on retry since it already exists)
        if (!isRetry) {
          updateChatOptimistically({
            id: `temp-user-${uuidv4()}`,
            role: "user",
            content: newQuestion,
          });
        }

        const token = await authState.getTokenSilently();

        // Track the chatId returned by the backend (may be new if no chatId was sent)
        let responseChatId = null;

        streamAIResponse(
          {
            prompt: newQuestion,
            aiChatId: chatId,
          },
          {
            tenantId,
            apiUrl,
            agentName: AGENTCORE_NAMES.ASK_TEAMFORM,
            headers: {
              Authorization: `Bearer ${token}`,
              "x-workspace-id": workspaceId,
            },

            onError: (e) => {
              setError(e);
              setIsThinking(false);
              setComplete(true);
              setIsSubmitting(false);
            },

            onMessageChunk: (chunkEvent) => {
              if (chunkEvent?.message) {
                if (!responseChatId && chunkEvent.message.aiChatId) {
                  responseChatId = chunkEvent.message.aiChatId;
                }

                // Use the formatted HTML content from the top-level 'content' field
                // instead of the raw markdown in message.content
                const messageWithFormattedContent = {
                  ...chunkEvent.message,
                  content: chunkEvent.content || chunkEvent.message.content,
                };
                updateChatOptimistically(messageWithFormattedContent);
                setIsThinking(false);
              }
            },
            onDone: () => {
              // If the backend created a new chat, adopt its ID and refresh the chat list
              if (responseChatId && responseChatId !== chatId) {
                adoptChatId(responseChatId);
                refetchChats();
              }

              setComplete(true);
              setIsSubmitting(false);
              setIsThinking(false);
            },
            onMessage: (messageEvent) => {
              if (messageEvent?.final_message) {
                updateChatOptimistically(messageEvent?.final_message);
              }

              if (
                messageEvent?.final_message?.content?.includes("<dashboard")
              ) {
                const dashboardId = messageEvent?.final_message?.content?.match(
                  /<dashboard[^>]*\bid="([^"]+)"/
                )?.[1];
                const title = messageEvent?.final_message?.content?.match(
                  /<dashboard[^>]*\btitle="([^"]+)"/
                )?.[1];

                if (dashboardId || title) {
                  EventBus.emit(EVENTS.AI_DASHBOARD_UPDATED, {
                    dashboardId,
                    title,
                  });
                }
              }
            },
          }
        );
      } catch (e) {
        setError(e);
        setIsSubmitting(false);
        setComplete(true);
        setIsThinking(false);
      }
    },
    [
      authState,
      chatId,
      tenantId,
      apiUrl,
      workspaceId,
      updateChatOptimistically,
      adoptChatId,
      refetchChats,
    ]
  );

  const onSendQuestion = useCallback(async () => {
    if (editingMessageId) {
      const messageToUpdate = messages.find((m) => m.id === editingMessageId);
      if (!messageToUpdate) {
        return;
      }
      setIsSubmitting(true);
      setComplete(false);
      setIsThinking(true);
      setError(null);
      setEditingMessageId(null);
      setQuestion("");

      // Update the local message content optimistically (backend persists it)
      updateChatOptimistically({
        ...messageToUpdate,
        content: question,
      });

      invokeAIResponse(question, { isRetry: true });
    } else {
      invokeAIResponse(question);
    }
  }, [
    editingMessageId,
    question,
    messages,
    updateChatOptimistically,
    invokeAIResponse,
  ]);

  const lastUserMessage = useMemo(() => {
    const userMessage = _.findLast(messages, (m) => {
      return m.role === "user";
    });

    const lastMessage = _.last(messages);

    if (
      lastMessage?.role === "assistant" &&
      lastMessage.content?.length === 0
    ) {
      return userMessage;
    }

    if (lastMessage?.role === "user") {
      return lastMessage;
    }

    return null;
  }, [messages]);

  const canRetryOrEdit = useMemo(
    () => (!!error && !isSubmitting) || (!!lastUserMessage && !isSubmitting),
    [error, isSubmitting, lastUserMessage]
  );

  const onRetry = useCallback(() => {
    if (!lastUserMessage?.content) {
      return;
    }
    setIsSubmitting(true);
    setComplete(false);
    setIsThinking(true);
    setError(null);
    invokeAIResponse(lastUserMessage.content, { isRetry: true });
  }, [invokeAIResponse, lastUserMessage]);

  const onEditLastMessage = useCallback(() => {
    if (!lastUserMessage) {
      return;
    }
    setQuestion(lastUserMessage.content || "");
    setEditingMessageId(lastUserMessage.id);
    setError(null);
  }, [lastUserMessage]);

  const onCancelEdit = useCallback(() => {
    setEditingMessageId(null);
    setQuestion("");
  }, []);

  const showLoader = useMemo(() => {
    return isSubmitting;
  }, [isSubmitting]);

  const values = useMemo(
    () => ({
      canRetryOrEdit,
      chatId,
      editingMessageId,
      messages,
      previousMessages,
      complete,
      hasError: !!error,
      setUserHasScrolledUp,
      userHasScrolledUp,
      question,
      setQuestion,
      onSessionClear,
      onSendQuestion,
      showLoader,
      onRetry,
      onEditLastMessage,
      onCancelEdit,
      error,
      setError,
      onStartNewChat,
      chats,
      chatsLoading,
      onSelectChat,
      isThinking,
      onSubmitMessageFeedback,
    }),
    [
      canRetryOrEdit,
      chatId,
      editingMessageId,
      messages,
      previousMessages,
      complete,
      setUserHasScrolledUp,
      userHasScrolledUp,
      question,
      setQuestion,
      onSessionClear,
      onSendQuestion,
      showLoader,
      onRetry,
      onEditLastMessage,
      onCancelEdit,
      error,
      setError,
      onStartNewChat,
      chats,
      chatsLoading,
      onSelectChat,
      isThinking,
      onSubmitMessageFeedback,
    ]
  );

  if (!aiEnabled) {
    return null;
  }

  return (
    <AIChatContext.Provider value={values}>{children}</AIChatContext.Provider>
  );
};

AIChatProvider.propTypes = {
  children: PropTypes.node.isRequired,
  sourceId: PropTypes.string.isRequired,
  // subjectId: PropTypes.string.isRequired,
};
